import React, { useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import SearchDCInput from '../../search-dc/SearchDCInput';
import '../supplyChainStyle.scss';
import { deviceOnboardingInit, deviceOnboardingClear } from '../redux/actions';
import { isValidIpAddress, isValidEmailList, findInvalidEmail } from '../../../common/Utils/validators';
import { setSelectedDCList } from '../../search-dc/redux/actions';

const Onboarding = () => {
    const toast = useRef(null);
    const selectedDCs = useSelector(state => state.searchDC.selectedDCList);
    const onboardingSuccess = useSelector(state => state.supplyChain.deviceOnboarding.success);
    const onboardingError = useSelector(state => state.supplyChain.deviceOnboarding.error);
    const onboardingLoading = useSelector(state => state.supplyChain.deviceOnboarding.loading);
    const dispatch = useDispatch();

    useEffect(() => {
        if (onboardingSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Device onboarded successfully', life: 3000 });
            clearForm();
            dispatch(deviceOnboardingClear());
        } else if (onboardingError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: onboardingError || 'Failed to onboard device', life: 3000 });
            dispatch(deviceOnboardingClear());
        }
    }, [onboardingSuccess, onboardingError]); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        values,
        touched,
        handleChange,
        handleSubmit,
        errors,
        resetForm
    } = useFormik({
        initialValues: {
            storeCode: '',
            deviceName: '',
            hostname: '',
            ipAddress: '',
            emailAddress: '',
            assetLocation: '',
            description: ''
        },
        validate: (data) => {
            let errors = {};
            validateDeviceName(data, errors);
            validateDCCodes(data, errors);
            validateIpAddress(data, errors);
            validateEmailAddress(data, errors);
            validateDescription(data, errors);

            return errors;
        },
        onSubmit: (data) => {
            const storeCode = selectedDCs?.[0]?.code ?? '';
            dispatch(deviceOnboardingInit({ ...data, storeCode }));
        }
    });

    const validateDeviceName = (data, errors) => {
        if (!data.deviceName) {
            errors.deviceName = "Please enter the Device Name";
        }
    }
    
    
    const validateDCCodes = (_data, errors) => {
        if (!selectedDCs || selectedDCs.length === 0) {
            errors.storeCode = "Please select the distribution center";
        }
    }
    
    const validateIpAddress = (data, errors) => {
        if (!data.ipAddress) {
            errors.ipAddress = "Please enter the IP address";
        } else if (!isValidIpAddress(data.ipAddress)) {
            errors.ipAddress = "Invalid IP Address format";
        }
    }

    const validateEmailAddress = (data, errors) => {
        if (!data.emailAddress) {
            errors.emailAddress = "Please enter the Email Address";
        } else if (data.emailAddress.length > 500) {
            errors.emailAddress = "Email Address cannot exceed 500 characters";
        } else if (!isValidEmailList(data.emailAddress)) {
            const badEmail = findInvalidEmail(data.emailAddress);
            errors.emailAddress = `Invalid Email Address format for ${badEmail}`;
        }
    }
    
    const validateDescription = (data, errors) => {
        if (data.description && data.description.length > 500) {
            errors.description = "Description cannot exceed 500 characters";
        }
    }

    
    const clearForm = () => {
        resetForm();
        dispatch(setSelectedDCList([]));
    };

    return (
        <div className="supply-chain-container container-fluid">
            <Toast ref={toast} position="bottom-center" />
            <p className="page-label">
                Supply Chain
                <i className="pi pi-angle-right"/>
                Onboarding
            </p>
            <form className="container-fluid" onSubmit={handleSubmit}>
                <div className="row input-field">
                    <div className="col-12 col-lg-2 ">
                        <label className="required">Device Name</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputText
                                name="deviceName"
                                value={values.deviceName}
                                onChange={handleChange}
                                placeholder="Enter Device Name"
                            />
                            <br/>
                            {!!touched.deviceName && errors.deviceName && <span className="p-error">{errors.deviceName}</span>}
                        </span>
                    </div>
                </div>
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label>Hostname</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputText
                                name="hostname"
                                value={values.hostname}
                                onChange={handleChange}
                                placeholder="Enter Host Name"
                            />
                            <br/>
                            {!!touched.hostname && errors.hostname && <span className="p-error">{errors.hostname}</span>}
                        </span>
                    </div>
                </div>
                
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label className="required">DC Name</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span className='w-100'>
                            <SearchDCInput
                                showButton={false}
                                singleSelect={true}
                                placeholder="Select the Distribution Center from dropdown"
                            />
                          
                            {!!touched.storeCode && errors.storeCode && <span className="p-error">{errors.storeCode}</span>}
                    </span></div>
                </div>
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label className="required">IP Address</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputText
                                name="ipAddress"
                                value={values.ipAddress}
                                onChange={handleChange}
                                placeholder="Enter IP Address (e.g., 192.168.1.1)"
    
                            />
                            <br/>
                            {!!touched.ipAddress && errors.ipAddress && <span className="p-error">{errors.ipAddress}</span>}
                        </span>
                    </div>
                </div>
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label className="required">Email</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputTextarea
                                name="emailAddress"
                                value={values.emailAddress}
                                onChange={handleChange}
                                placeholder="Enter Email Address(es)"
                                maxLength={500}
                            />
                            <div className="char-counter-row">
                                <strong>Note: For multiple emails use ';' as separator</strong>
                                <div className="char-counter">{values.emailAddress?.length || 0}/500</div>
                            </div>
                       
                            {!!touched.emailAddress && errors.emailAddress && <span className="p-error">{errors.emailAddress}</span>}
                        </span>
                    </div>
                </div>
                
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label>Asset Location</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputText
                                name="assetLocation"
                                value={values.assetLocation}
                                onChange={handleChange}
                                placeholder="Enter Asset Location"
                            />
                            <br/>
                            {!!touched.assetLocation && errors.assetLocation && <span className="p-error">{errors.assetLocation}</span>}
                        </span>
                    </div>
                </div>
                <div className="row input-field">
                    <div className="col-12 col-lg-2">
                        <label>Description</label>
                    </div>
                    <div className="col-12 col-lg-10">
                        <span>
                            <InputTextarea
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                placeholder="Enter Description (max 500 characters)"
                                maxLength={500}
                            />
                            <br/>

                            <div className="char-counter-row" style={{ justifyContent: "right" }}>
                                <p className="char-counter" >{values.description?.length || 0}/500</p>
                            </div>
                            {!!touched.description && errors.description && <span className="p-error">{errors.description}</span>}
                        </span>
                    </div>
                </div>
                <div className="row input-field">
                    <div className='col-auto'>
                        <Button label="Save" type="submit" disabled={onboardingLoading} loading={onboardingLoading} style={{marginRight: "1.25rem"}}/>
                    </div>
                    <div className='col-auto'>
                        <Button label="Clear" type="button" onClick={clearForm} disabled={onboardingLoading} className="p-button-secondary"/>
                    </div>
                </div>
            </form>
        </div>
    );
}
export default Onboarding;